export const DEVICE_COMMAND_OPERATOR_COPY = {
  sending: "Sending…",
  downloading: "Downloading…",
  successful: "Successful",
  unsuccessful: "Unsuccessful",
} as const;

export type DeviceCommandOperatorState =
  | "sending"
  | "downloading"
  | "successful"
  | "unsuccessful";

export const DEVICE_COMMAND_RESTART_TIMEOUT_MS = 30_000;
export const DEVICE_COMMAND_SPEED_TEST_TIMEOUT_MS = 45_000;
/**
 * Exception 3: browser does NOT own update lifecycle.
 * Poll until Fleet returns terminal / orphan — align with 6h orphan TTL.
 * Restart commands still use the short restart timeout.
 */
export const DEVICE_COMMAND_UPDATE_ORPHAN_POLL_MS = 6 * 60 * 60_000;

/** Align with Fleet PROGRESS_STALE_MS for package-owned media Apply. */
export const DEVICE_COMMAND_SCREENSAVER_APPLY_POLL_MS = 35 * 60_000;

export function deviceCommandTimeoutMs(
  command: string,
  extras?: { action?: "apply" | "enable" | "disable" }
): number {
  // Enable/disable are quick IPC; Apply downloads inside the screensaver package.
  if (command === "update_kk_screensaver") {
    if (extras?.action === "enable" || extras?.action === "disable") {
      return DEVICE_COMMAND_RESTART_TIMEOUT_MS;
    }
    return DEVICE_COMMAND_SCREENSAVER_APPLY_POLL_MS;
  }
  if (
    command === "update_kk_app_meta" ||
    command === "report_kk_app_meta" ||
    // T24 — mirror app-meta 30s. Without this, update_* would fall into the
    // 6h orphan-poll bucket below.
    command === "update_kk_music_schedule" ||
    command === "report_kk_music_schedule" ||
    command === "report_device_info"
  ) {
    return DEVICE_COMMAND_RESTART_TIMEOUT_MS;
  }
  if (command === "test_download_speed") {
    return DEVICE_COMMAND_SPEED_TEST_TIMEOUT_MS;
  }
  return command.startsWith("update_")
    ? DEVICE_COMMAND_UPDATE_ORPHAN_POLL_MS
    : DEVICE_COMMAND_RESTART_TIMEOUT_MS;
}

const ACTIVE_JOBS_KEY = "kk.sa.deviceCommandJobs.v1";

export type PersistedDeviceCommandJob = {
  commandId: string;
  command: string;
  deviceId?: string;
  startedAt: number;
};

/** Persist commandIds so leave/reload can resume Fleet polling (Exception 3). */
export function persistDeviceCommandJobs(jobs: PersistedDeviceCommandJob[]): void {
  try {
    sessionStorage.setItem(ACTIVE_JOBS_KEY, JSON.stringify(jobs));
  } catch {
    // ignore quota / private mode
  }
}

export function loadPersistedDeviceCommandJobs(): PersistedDeviceCommandJob[] {
  try {
    const raw = sessionStorage.getItem(ACTIVE_JOBS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearPersistedDeviceCommandJobs(): void {
  try {
    sessionStorage.removeItem(ACTIVE_JOBS_KEY);
  } catch {
    // ignore
  }
}

/**
 * HTTP 202 / published / sent never returns successful.
 * Fleet is source of truth for downloading / terminal / stale / orphan.
 */
export async function waitForDeviceCommandOperatorResult(args: {
  commandId?: string | null;
  command: string;
  action?: "apply" | "enable" | "disable";
  getStatus: (
    commandId: string
  ) => Promise<{
    operatorState?: string;
    activeContentId?: string | null;
    activeContentVersion?: string | null;
  } | null>;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  pollMs?: number;
  onState?: (state: DeviceCommandOperatorState) => void;
}): Promise<DeviceCommandOperatorState> {
  const { commandId, command, getStatus } = args;
  if (!commandId) {
    args.onState?.("unsuccessful");
    return "unsuccessful";
  }
  const now = args.now || Date.now;
  const sleep = args.sleep || ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const pollMs = args.pollMs ?? 1000;
  const deadline = now() + deviceCommandTimeoutMs(command, { action: args.action });
  let lastEmitted: DeviceCommandOperatorState | null = null;
  while (now() <= deadline) {
    const status = await getStatus(commandId);
    const state = (status?.operatorState || "sending") as DeviceCommandOperatorState;
    if (state === "successful" || state === "unsuccessful") {
      args.onState?.(state);
      return state;
    }
    const mid: DeviceCommandOperatorState =
      state === "downloading" ? "downloading" : "sending";
    if (mid !== lastEmitted) {
      lastEmitted = mid;
      args.onState?.(mid);
    }
    if (now() + pollMs > deadline) break;
    await sleep(pollMs);
  }
  args.onState?.("unsuccessful");
  return "unsuccessful";
}
